import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatAmount } from '../../utils/formatAmount';
import ReportService from './ReportService';

const TYPE_LABEL = { orderer_fee: 'Orderer Fee', merpay_topup: 'Merpay Topup' };
const TYPE_COLOR = { orderer_fee: 'warning', merpay_topup: 'info' };

const today = () => new Date().toISOString().slice(0, 10);

export default function OrdererPaymentSection({ onSaved }) {
  const [payments, setPayments] = useState([]);
  const [orderers, setOrderers] = useState([]);
  const [form, setForm] = useState({ orderByOptionId: '', amount: '', paymentDate: today(), type: 'orderer_fee', note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [p, o] = await Promise.all([ReportService.getOrdererPayments(), ReportService.getOrderers()]);
    setPayments(p.data);
    setOrderers(o.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.orderByOptionId || !form.amount || !form.paymentDate || !form.type) return;
    setSaving(true);
    try {
      await ReportService.createOrdererPayment(form);
      setForm({ orderByOptionId: '', amount: '', paymentDate: today(), type: 'orderer_fee', note: '' });
      await load();
      if (onSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    await ReportService.deleteOrdererPayment(id);
    await load();
    if (onSaved) onSaved();
  };

  return (
    <div style={{ marginTop: 32 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Orderer & Merpay Payments</Typography>

      {/* Add form */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Orderer / Account</InputLabel>
          <Select
            value={form.orderByOptionId}
            label="Orderer / Account"
            onChange={e => setForm(f => ({ ...f, orderByOptionId: e.target.value }))}
          >
            {orderers.map(o => (
              <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={form.type}
            label="Type"
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
          >
            <MenuItem value="orderer_fee">Orderer Fee</MenuItem>
            <MenuItem value="merpay_topup">Merpay Topup</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Amount (THB)"
          type="number"
          size="small"
          value={form.amount}
          onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
          sx={{ width: 150 }}
        />

        <TextField
          label="Date"
          type="date"
          size="small"
          value={form.paymentDate}
          onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />

        <TextField
          label="Note (optional)"
          size="small"
          value={form.note}
          onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
          sx={{ width: 200 }}
        />

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.orderByOptionId || !form.amount}
        >
          {saving ? 'Saving…' : 'Add'}
        </Button>
      </Paper>

      {/* Payment list */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#424242' }}>
              {['Date', 'Orderer / Account', 'Type', 'Amount (THB)', 'Note', ''].map(h => (
                <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: '#999', py: 3 }}>No payments recorded yet</TableCell>
              </TableRow>
            )}
            {payments.map(p => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontSize: 12 }}>{p.paymentDate}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{p.orderer?.label || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  <Chip label={TYPE_LABEL[p.type]} color={TYPE_COLOR[p.type]} size="small" />
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{formatAmount(Number(p.amount))}</TableCell>
                <TableCell sx={{ fontSize: 12, color: '#666' }}>{p.note || '—'}</TableCell>
                <TableCell>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
